import {useEffect, useMemo, useRef, useState} from 'react';
import {toVbaSexCode, type GenderCode} from '@common/gender';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {GenderSelect, MEMBER_GENDER_CODES} from '@client/components/GenderSelect';
import {MemberLifecycleDialogFrame} from '@client/pages/client/member-profile/MemberLifecycleDialogFrame';

/** VBA relationship codes valid for a dependent. */
const RELATIONSHIP_OPTIONS = [
  {value: '02', label: 'Spouse'},
  {value: '03', label: 'Child'},
  {value: '19', label: 'Dependent'},
] as const;

interface AddDependentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  groupId: string;
  /** Composed id of the subscriber (or any household member). */
  memberId: string;
  /** Products the subscriber carries, offered as the dependent's starting coverage. */
  availableProducts: {productId: string; name: string}[];
}

function firstOfNextMonth(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString().slice(0, 10);
}

const EMPTY_FORM = {
  dateOfBirth: '',
  firstName: '',
  lastName: '',
  middleName: '',
  relationship: '' as '' | '02' | '03' | '19',
  sexCode: '' as GenderCode | '',
  ssn: '',
};

export function AddDependentDialog({
  open,
  onClose,
  onSuccess,
  groupId,
  memberId,
  availableProducts,
}: AddDependentDialogProps) {
  const {toast} = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [effectiveDate, setEffectiveDate] = useState(firstOfNextMonth());
  const [productIds, setProductIds] = useState<string[]>([]);

  const productsInitialized = useRef(false);

  useEffect(() => {
    productsInitialized.current = false;
    if (!open) return;
    setForm(EMPTY_FORM);
    setEffectiveDate(firstOfNextMonth());
    setProductIds([]);
  }, [open]);

  // Default to mirroring the subscriber's coverage, once per open. availableProducts is rebuilt
  // every render, so later refetches must not overwrite the user's selections.
  useEffect(() => {
    if (!open || productsInitialized.current || availableProducts.length === 0) return;
    productsInitialized.current = true;
    setProductIds(availableProducts.map((p) => p.productId));
  }, [open, availableProducts]);

  const setField = (patch: Partial<typeof EMPTY_FORM>) => setForm((prev) => ({...prev, ...patch}));

  const ssnDigits = form.ssn.replace(/\D/g, '');
  const canSubmit = useMemo(
    () =>
      !!form.firstName.trim() &&
      !!form.lastName.trim() &&
      !!form.relationship &&
      !!form.dateOfBirth &&
      ssnDigits.length === 9 &&
      !!effectiveDate,
    [form, ssnDigits, effectiveDate],
  );

  const addDependent = trpc.group.vbaMembers.addDependent.useMutation({
    onSuccess: (result) => {
      toast({
        title: 'Dependent added',
        description: result.enrolledProducts.length
          ? `Enrolled in ${result.enrolledProducts.length} product(s).`
          : 'Created without product coverage.',
      });
      onSuccess();
      onClose();
    },
    onError: (err) =>
      toast({title: 'Could not add dependent', description: err.message, variant: 'destructive'}),
  });

  const toggleProduct = (productId: string, checked: boolean) =>
    setProductIds((prev) =>
      checked ? [...new Set([...prev, productId])] : prev.filter((p) => p !== productId),
    );

  const handleSubmit = () => {
    if (!form.relationship) return;
    addDependent.mutate({
      dateOfBirth: form.dateOfBirth,
      effectiveDate,
      firstName: form.firstName.trim(),
      groupId,
      id: memberId,
      lastName: form.lastName.trim(),
      middleName: form.middleName.trim() || null,
      products: productIds.filter((id) => availableProducts.some((p) => p.productId === id)),
      relationship: form.relationship,
      ssn: ssnDigits,
      ...(form.sexCode ? {sexCode: toVbaSexCode(form.sexCode)} : {}),
    });
  };

  return (
    <MemberLifecycleDialogFrame
      open={open}
      onClose={onClose}
      title='Add dependent'
      description='Create a dependent under this subscriber and enroll them in coverage. This writes to VBA and is logged.'
      contentClassName='max-h-[60vh] space-y-4 overflow-y-auto py-2'
      contentMaxWidthClassName='sm:max-w-lg'
      cancelDisabled={addDependent.isPending}
      submitDisabled={!canSubmit || addDependent.isPending}
      submitLabel={addDependent.isPending ? 'Adding…' : 'Add dependent'}
      onSubmit={handleSubmit}
    >
      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1.5'>
          <Label htmlFor='dep-first'>First name</Label>
          <Input
            id='dep-first'
            value={form.firstName}
            onChange={(e) => setField({firstName: e.target.value})}
            maxLength={25}
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='dep-last'>Last name</Label>
          <Input
            id='dep-last'
            value={form.lastName}
            onChange={(e) => setField({lastName: e.target.value})}
            maxLength={25}
          />
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1.5'>
          <Label>Relationship</Label>
          <Select
            value={form.relationship}
            onValueChange={(v) => setField({relationship: v as typeof form.relationship})}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_OPTIONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='space-y-1.5'>
          <Label>Gender</Label>
          <GenderSelect
            codes={MEMBER_GENDER_CODES}
            placeholder='Select'
            value={form.sexCode}
            onValueChange={(sexCode) => setField({sexCode})}
          />
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <div className='space-y-1.5'>
          <Label htmlFor='dep-dob'>Date of birth</Label>
          <Input
            id='dep-dob'
            type='date'
            value={form.dateOfBirth}
            onChange={(e) => setField({dateOfBirth: e.target.value})}
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='dep-ssn'>SSN</Label>
          <Input
            id='dep-ssn'
            value={form.ssn}
            onChange={(e) => setField({ssn: e.target.value})}
            placeholder='123-45-6789'
            maxLength={11}
          />
          {!!form.ssn && ssnDigits.length !== 9 && (
            <p className='text-xs text-red-600'>SSN must be 9 digits.</p>
          )}
        </div>
      </div>

      <div className='space-y-1.5'>
        <Label htmlFor='dep-effective'>Effective date</Label>
        <Input
          id='dep-effective'
          type='date'
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
        />
      </div>

      {availableProducts.length > 0 && (
        <div className='space-y-2'>
          <Label>Coverage</Label>
          <div className='space-y-2 rounded-lg border border-gray-200 p-3'>
            {availableProducts.map((p) => (
              <div key={p.productId} className='flex items-center gap-2'>
                <Checkbox
                  id={`dep-product-${p.productId}`}
                  checked={productIds.includes(p.productId)}
                  onCheckedChange={(c) => toggleProduct(p.productId, Boolean(c))}
                />
                <Label htmlFor={`dep-product-${p.productId}`} className='font-normal'>
                  {p.name}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}
    </MemberLifecycleDialogFrame>
  );
}
